import ActionTextIcon from "@/components/icons/ActionTextIcon";
import InfoIcon from "@/components/icons/InfoIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency, formatLocaleNettoPoints } from "@/utils/formatUtils";
import { URLQuery } from "@/utils/queryUtils";
import { tInterval } from "@/utils/translationUtils";
import { isDefined, numberOrZero } from "@/utils/utils";
import { Descriptions, Dropdown, Popconfirm, Popover, Space, Table, Tooltip } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";
import {
  useDeleteAgentCommissionsOutput,
  useDownloadCommissionsOutputsExport,
  useFilterCommissionsOutputs,
} from "../../../../../common/api/queries";
import { ExportFileType } from "../../../../../common/enums";
import { Permission } from "../../../../../common/security/enums";
import { UUID } from "../../../../../typings/global";
import { AgentTypeTagWithName } from "../../../../agent/components/tags/AgentTypeTagWithName";
import { AGENT_ROUTE_PATHS } from "../../../../agent/paths";
import { formatAgentIdNumber } from "../../../../agent/utils";
import { useHasPermissions, useIsCurrentAgentTypePlus } from "../../../../auth/hooks";
import { PaidCommissionDetailTable } from "../../../batch/components/tables/PaidCommissionDetailTable";
import { CommissionsBatchStep } from "../../../batch/enums";
import { CommissionsOutput } from "../../../batch/types";
import { showPaidCommissionsPopover } from "../../../batch/utils";
import { CommissionBatchName } from "../../../components/CommissionBatchName";
import { CommissionsOutputFilterPageRequest } from "../../types";
import { CommissionsOutputsTogglePaidActions } from "../CommissionsOutputsTogglePaidActions";
import { CommissionsOutputModalForm } from "../forms/CommissionsOutputModalForm";
import { OutputCalculatedCommissionModal } from "../modals/OutputCalculatedCommissionModal";
import { OutputSpecialCommissionModal } from "../modals/OutputSpecialCommissionModal";
import { PaidInBatchField } from "../PaidInBatchField";

type Props = {
  searchParams: CommissionsOutputFilterPageRequest;
};

export const CommissionsOutputsTable = ({ searchParams }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isTopAgentPlus = useIsCurrentAgentTypePlus();
  const hasReadAgentPermission = useHasPermissions(Permission.AGENT_READ);
  const hasCommissionsManagePermission = useHasPermissions(Permission.COMMISSIONS_MANAGE);

  const [selectedOutputs, setSelectedOutputs] = useState<CommissionsOutput[]>([]);
  const [commissionsOutputToUpdate, setCommissionsOutputToUpdate] = useState<CommissionsOutput>();
  const [outputToShowCalculatedCommissionsFor, setOutputToShowCalculatedCommissionsFor] = useState<{
    output: CommissionsOutput;
    agentId: UUID;
  }>();

  const [outputToShowSpecialCommissionsFor, setOutputToShowSpecialCommissionsFor] = useState<{
    output: CommissionsOutput;
    agentId: UUID;
  }>();

  const { data } = useFilterCommissionsOutputs(searchParams);
  const downloadCommissionsOutputsExport = useDownloadCommissionsOutputsExport();
  const deleteAgentCommissionsOutput = useDeleteAgentCommissionsOutput();

  const columns: ColumnsType<CommissionsOutput> = useMemo(
    () => [
      {
        key: "commissionsBatch",
        title: t("commissions.batch.attrs.commission.batch"),
        width: 140,
        ellipsis: { showTitle: false },
        render: (_, record) =>
          record.batch ? (
            <CommissionBatchName batchName={record.batch.name} batchId={record.batch.id} showLink />
          ) : (
            <Space>
              <span className="sub-header-info">{t("commissions.outputs.helpers.manualClaim")}</span>
              {record.note && <InfoIcon tooltip={record.note} />}
            </Space>
          ),
      },
      {
        key: "gainer",
        title: t("commissions.batch.attrs.attachment.gainer"),
        width: 160,
        ellipsis: { showTitle: false },
        render: (_, record) => (
          <AgentTypeTagWithName
            agent={record.gainer}
            ellipsis
            showLink={hasReadAgentPermission}
            showRepresentatives
            hideIdNumber
            linkTarget="_blank"
          />
        ),
      },
      {
        key: "idNumber",
        title: t("agent.attrs.idNumber"),
        width: 70,
        render: (_, record) => formatAgentIdNumber(record.gainer),
      },
      {
        key: "calculatedCommissionAmount",
        title: t("commissions.batch.attrs.attachment.calculatedCommissionAmount"),
        align: "right",
        width: 125,
        render: (_, record) =>
          !record.batch?.stepChangeInProgress && record.batch ? (
            <span>
              <ActionTextIcon
                text={formatLocaleCurrency(record.calculatedCommissionAmount)}
                onClick={() => setOutputToShowCalculatedCommissionsFor({ output: record, agentId: record.gainer.id })}
                color="blue"
              />
            </span>
          ) : (
            formatLocaleCurrency(record.calculatedCommissionAmount)
          ),
      },
      {
        key: "nettoPointAmount",
        title: t("commissions.batch.helpers.nettoPointAmount"),
        align: "right",
        width: 115,
        render: (_, record) => (
          <Popover
            content={
              <Descriptions
                column={1}
                size="small"
                bordered
                styles={{ content: { textAlign: "right" } }}
                items={[
                  {
                    key: "individualPointAmount",
                    label: t("commissions.batch.attrs.attachment.individualPointAmount"),
                    children: formatLocaleNettoPoints(record.individualPointAmount, isTopAgentPlus ? 6 : 2),
                  },
                  {
                    key: "groupPointAmount",
                    label: t("commissions.batch.attrs.attachment.groupPointAmount"),
                    children: formatLocaleNettoPoints(record.groupPointAmount, isTopAgentPlus ? 6 : 2),
                  },
                  {
                    key: "totalPointAmount",
                    label: t("commissions.batch.attrs.attachment.totalPointAmount"),
                    children: formatLocaleNettoPoints(record.totalPointAmount, isTopAgentPlus ? 6 : 2),
                  },
                ]}
              />
            }
          >
            {formatLocaleNettoPoints(record.totalPointAmount)}
          </Popover>
        ),
      },
      {
        key: "specialCommissionAmount",
        title: t("commissions.batch.attrs.attachment.specialCommissionAmount"),
        align: "right",
        width: 125,
        render: (_, record) => (
          <Tooltip
            title={tInterval(
              "commissions.batch.helpers.specialCommissionCountTooltip_interval",
              record.specialCommissionCount,
            )}
          >
            {!record.batch?.stepChangeInProgress && record.specialCommissionCount > 0 ? (
              <span>
                <ActionTextIcon
                  text={formatLocaleCurrency(record.specialCommissionAmount)}
                  onClick={() => setOutputToShowSpecialCommissionsFor({ output: record, agentId: record.gainer.id })}
                  color="blue"
                />
              </span>
            ) : (
              formatLocaleCurrency(record.specialCommissionAmount)
            )}
          </Tooltip>
        ),
      },
      {
        key: "bailAccount",
        title: t("commissions.batch.attrs.attachment.bailAccount"),
        children: [
          {
            key: "bailAccountOpeningBalance",
            title: t("commissions.batch.attrs.attachment.bailAccountOpeningBalance"),
            align: "right",
            width: 115,
            render: (_, record) =>
              isDefined(record.bailAccountOpeningBalance)
                ? formatLocaleCurrency(record.bailAccountOpeningBalance)
                : undefined,
          },
          {
            key: "bailAccountAutomaticMovement",
            title: t("commissions.batch.attrs.attachment.bailAccountAutomaticMovement"),
            align: "right",
            width: 115,
            render: (_, record) =>
              isDefined(record.bailAccountAutomaticMovement)
                ? formatLocaleCurrency(record.bailAccountAutomaticMovement)
                : undefined,
          },
          {
            key: "bailAccountManualMovement",
            title: t("commissions.batch.attrs.attachment.bailAccountManualMovement"),
            align: "right",
            width: 115,
            render: (_, record) =>
              isDefined(record.bailAccountManualMovement)
                ? formatLocaleCurrency(record.bailAccountManualMovement)
                : undefined,
          },
          {
            key: "bailAccountClosingBalance",
            title: t("commissions.batch.attrs.attachment.bailAccountClosingBalance"),
            align: "right",
            width: 115,
            render: (_, record) =>
              isDefined(record.bailAccountClosingBalance)
                ? formatLocaleCurrency(record.bailAccountClosingBalance)
                : undefined,
          },
        ],
      },
      {
        key: "debtAccount",
        title: t("commissions.batch.attrs.attachment.debtAccount"),
        children: [
          {
            key: "debtAccountOpeningBalance",
            title: t("commissions.batch.attrs.attachment.debtAccountOpeningBalance"),
            align: "right",
            width: 115,
            render: (_, record) => formatLocaleCurrency(record.debtAccountOpeningBalance),
          },
          {
            key: "debtAccountAutomaticMovement",
            title: t("commissions.batch.attrs.attachment.debtAccountAutomaticMovement"),
            align: "right",
            width: 115,
            render: (_, record) => formatLocaleCurrency(record.debtAccountAutomaticMovement),
          },
          {
            key: "debtAccountManualMovement",
            title: t("commissions.batch.attrs.attachment.debtAccountManualMovement"),
            align: "right",
            width: 115,
            render: (_, record) => formatLocaleCurrency(record.debtAccountManualMovement),
          },
          {
            key: "debtAccountClosingBalance",
            title: t("commissions.batch.attrs.attachment.debtAccountClosingBalance"),
            align: "right",
            width: 115,
            render: (_, record) => formatLocaleCurrency(record.debtAccountClosingBalance),
          },
        ],
      },
      {
        key: "paidCommissionAmount",
        title: t("commissions.batch.attrs.attachment.paidCommissionAmount"),
        align: "right",
        fixed: "right",
        width: 130,
        render: (_, record) => (
          <Popover
            content={showPaidCommissionsPopover(record) ? <PaidCommissionDetailTable data={record} /> : undefined}
          >
            <b>
              {showPaidCommissionsPopover(record)
                ? formatLocaleCurrency(
                    numberOrZero(record.calculatedCommissionAmountWithNoVat) +
                      numberOrZero(record.calculatedCommissionAmountWithoutVat),
                  )
                : formatLocaleCurrency(record.paidCommissionAmount)}
            </b>
          </Popover>
        ),
      },
      {
        key: "paidInBatch",
        align: "center",
        fixed: "right",
        width: 40,
        render: (_, record) => (
          <PaidInBatchField
            data={{
              paidInBatch: record.paidInBatch,
              id: record.id,
              optimisticLockVersion: record.optimisticLockVersion,
            }}
          />
        ),
      },
      {
        key: "actions",
        align: "right",
        fixed: "right",
        width: 90,
        render: (_, record) => (
          <Dropdown
            menu={{
              items: [
                {
                  key: "show",
                  label: (
                    <ActionTextIcon
                      path={generatePath(AGENT_ROUTE_PATHS.commissionsOutput.to, {
                        id1: record.gainer.id,
                        id2: record.id,
                      })}
                      disabled={(record.batch?.step === CommissionsBatchStep.FINISH && !record.file) || !record.batch}
                      target="_blank"
                      icon="eye"
                      color="blue"
                      text={t("common.show")}
                    />
                  ),
                },
                hasCommissionsManagePermission
                  ? {
                      key: "update",
                      label: (
                        <ActionTextIcon
                          icon="edit"
                          color="blue"
                          text={t("common.edit")}
                          disabled={!!record.batch || !!record.paidInBatch}
                          onClick={() => setCommissionsOutputToUpdate(record)}
                        />
                      ),
                    }
                  : undefined,
                hasCommissionsManagePermission
                  ? {
                      key: "delete",
                      label: (
                        <Popconfirm
                          title={t("commissions.outputs.helpers.deleteConfirm")}
                          icon={<PopconfirmDeleteIcon />}
                          okText={t("common.yes")}
                          cancelText={t("common.no")}
                          okType="danger"
                          disabled={!!record.batch || !!record.paidInBatch}
                          onConfirm={() =>
                            deleteAgentCommissionsOutput.mutate({ id1: record.gainer.id, id2: record.id })
                          }
                        >
                          <div>
                            <ActionTextIcon
                              icon="delete"
                              color="red"
                              text={t("common.delete")}
                              disabled={!!record.batch || !!record.paidInBatch}
                            />
                          </div>
                        </Popconfirm>
                      ),
                    }
                  : undefined,
              ].filter((item) => !!item),
            }}
            trigger={["click"]}
          >
            <span>
              <ActionTextIcon icon="menu-unfold" color="blue" text={t("common.options")} />
            </span>
          </Dropdown>
        ),
      },
    ],
    [isTopAgentPlus, hasReadAgentPermission, hasCommissionsManagePermission],
  );

  return (
    <>
      <Space className="margin-bottom-small">
        <b>{t("commissions.batch.helpers.filteredCommissionsSum")}:</b>
        {formatLocaleCurrency(data?.paidCommissionAmountSum ?? 0)}
      </Space>

      <div>
        <Space size="middle" className={data?.pageData.length ? "table-header-margin" : "margin-bottom-small"}>
          <Space>
            <ActionTextIcon
              icon="download"
              color="blue"
              text={t("common.exportXlsx")}
              onClick={() =>
                downloadCommissionsOutputsExport.mutate({ ...searchParams, exportFileType: ExportFileType.XLSX })
              }
            />

            <ActionTextIcon
              icon="download"
              color="green"
              text={t("common.exportCsv")}
              onClick={() =>
                downloadCommissionsOutputsExport.mutate({ ...searchParams, exportFileType: ExportFileType.CSV })
              }
            />
          </Space>

          {hasCommissionsManagePermission ? (
            <CommissionsOutputsTogglePaidActions
              onFinish={() => setSelectedOutputs([])}
              selectedOutputs={selectedOutputs}
            />
          ) : undefined}
        </Space>
      </div>

      <Table<CommissionsOutput>
        {...tableStandardProps()}
        columns={columns}
        scroll={{ x: 1950 }}
        dataSource={data?.pageData}
        rowSelection={
          hasCommissionsManagePermission
            ? {
                onChange: (_, selectedRows) => setSelectedOutputs(selectedRows),
                selectedRowKeys: selectedOutputs.map((commission) => commission.id),
                columnWidth: 35,
              }
            : undefined
        }
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount,
          onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
        }}
      />

      <OutputCalculatedCommissionModal
        isOpen={!!outputToShowCalculatedCommissionsFor}
        onClose={() => setOutputToShowCalculatedCommissionsFor(undefined)}
        agentId={outputToShowCalculatedCommissionsFor?.agentId ?? ""}
        commissionId={outputToShowCalculatedCommissionsFor?.output.id}
      />

      <OutputSpecialCommissionModal
        agentId={outputToShowSpecialCommissionsFor?.agentId ?? ""}
        commissionsOutput={outputToShowSpecialCommissionsFor?.output}
        onCancel={() => setOutputToShowSpecialCommissionsFor(undefined)}
      />

      <CommissionsOutputModalForm
        isOpen={!!commissionsOutputToUpdate}
        commissionOutput={commissionsOutputToUpdate}
        onCancel={() => setCommissionsOutputToUpdate(undefined)}
      />
    </>
  );
};
